import * as H from "history";
import * as qs from "qs";
import * as React from 'react';
import { TransitionGroup } from 'react-transition-group';
import './Dialog.css';
import { DialogClose } from "./DialogClose";
import { AnimationType, DialogTransition } from './DialogTransition';
import { addQueryString, convertPathToObject, hideScrollbar, removeQueryString, showScrollbar } from "./utils";

// We need to make sure the enter timeout is same as exit timeout for overlay
let instance: (DialogContainer | null) = null;
let overlayStartTime: number = -1;
const damp = 1;
const PRELOADER_ANIMATION_TIMEOUT = { enter: 100 * damp, exit: 150 * damp };
const COMP_ANIMATION_TIMEOUT = { enter: 300 * damp, exit: 300 * damp };
const OVERLAY_ANIMATION_TIMEOUT = { enter: COMP_ANIMATION_TIMEOUT.enter / 2, exit: COMP_ANIMATION_TIMEOUT.exit / 2 };
declare type ReactComponent = React.ComponentClass<any> | React.FC<any>;
export interface DialogPromise<T> extends Promise<T> {
    close: () => void;
}
declare type DialogParams = {
    key: number,
    isPreloader: boolean,
    Content: ReactComponent,
    ok: (returnValue: any) => void,
    cancel: () => void,
    props: { [key: string]: any },
    width?: string | number,
    defaultClass: string,
    className?: string,
    style?: React.CSSProperties,
    animation: AnimationType,
    context?: any,
    pathName?: string,
    showCloseButton: boolean
}
declare type DialogReturnType<T> = { cancelled: false, payLoad: T } | { cancelled: true };
function getDefaultClass(animation: AnimationType) {
    switch (animation) {
        case "fade": return "fade";
        case "slide-down": return "top-aside";
        case "slide-up": return "bottom-aside";
        case "slide-left": return "right-aside";
        case "slide-right": return "left-aside";
        case "modal": return "modal";
    }
}
function showComponentByRoute<T>(route: { path: string, component: any },
    options: {
        hash?: string,
        width?: string | number,
        className?: string,
        style?: React.CSSProperties,
        animation?: AnimationType,
        context?: any,
        showCloseButton?: boolean
    },
    ...params: Array<number | string | boolean | Date | null | undefined>) {
    const props = convertPathToObject(route.path, params);
    return showComponent(route.component, {
        hash: options.hash || route.path,
        width: options.width,
        className: options.className,
        style: options.style,
        animation: options.animation,
        context: options.context,
        path: route.path,
        showCloseButton: options.showCloseButton
    }, props);
}
function replaceComponent<T>(component: ReactComponent, path?: string) {
    if (!instance) {
        throw new Error("instance is not initialized");
    }
    const dialogs = instance.state.dialogs;
    let lastDialog = dialogs.pop()!;
    const newDialog = { ...lastDialog, Content: component, key: lastDialog.key * -1, pathName: path };
    lastDialog = { ...lastDialog, style: { ...lastDialog.style, bottom: 0, right: 0 }, defaultClass: "fade", animation: "fade" };
    instance.setState({ dialogs: [...dialogs, lastDialog] }, () => {
        const dialogs2 = instance!.state.dialogs
        //dialogs2.splice(dialogs2.length - 2, 1);
        dialogs2.pop();
        dialogs2.push(newDialog);
        instance!.setState({ dialogs: [...dialogs2] });
    });
    // instance.setState({dialogs: [...dialogs, lastDialog, newDialog]}, () => {
    //     //setTimeout(() => {
    //         const dialogs2 = instance!.state.dialogs
    //         dialogs2.splice(dialogs2.length - 2, 1);
    //         instance!.setState({dialogs: [...dialogs2]});
    //     //}, COMP_ANIMATION_TIMEOUT.enter);
    // });
}
function showComponent<T>(component: ReactComponent,
    options: {
        hash?: string,
        width?: string | number,
        className?: string,
        style?: React.CSSProperties,
        animation?: AnimationType,
        context?: any,
        path?: string,
        showCloseButton?: boolean
    },
    props?: any): DialogPromise<DialogReturnType<T>> {
    if (!instance) {
        throw new Error("instance is not initialized");
    }
    // const lastElement = instance.state.dialogs[instance.state.dialogs.length - 1];
    // if (lastElement && lastElement.isPreloader) throw new Error("Can't show dialog while preloader is active");
    if (!options.animation) options.animation = "slide-left";
    let cancelOuter: () => void = () => undefined;
    const promise = new Promise<DialogReturnType<T>>((resolve, reject) => {
        const history = instance!.props.historyFn();
        const unlisten = options.hash ? history.listen((location: H.Location, action: H.Action) => {
            const queryString = location.search ? location.search.replace("?", "") : ""
            const query2 = qs.parse(queryString);
            if (!("dialog" in query2)) {
                instance!.removeDialog();
                resolve({ cancelled: true });
                if (unlisten) unlisten();
            }
        }) : undefined;
        const ok = (returnValue: any) => {
            instance!.removeDialog();
            if (unlisten) {
                unlisten();
                const history = instance!.props.historyFn();
                history.replace(removeQueryString(history, `dialog`));
            }
            resolve({ cancelled: false, payLoad: returnValue });
        };
        const cancel = cancelOuter = () => {
            instance!.removeDialog();
            if (unlisten) {
                unlisten();
                const history = instance!.props.historyFn();
                history.replace(removeQueryString( history,`dialog`));
            }
            resolve({ cancelled: true });
        };
        instance!.addDialog({
            key: instance!.dialogCount + 1,
            isPreloader: false,
            width: options.width,
            defaultClass: getDefaultClass(options.animation!),
            className: options.className,
            style: options.style,
            animation: options.animation!,
            Content: component,
            ok, cancel, props,
            context: options.context,
            pathName: options.path,
            showCloseButton: options.showCloseButton || false
        });
        if (options.hash) {
            const history = instance!.props.historyFn();
            const newDialogRoute = addQueryString(history, `dialog`, options.hash);
            history.push(newDialogRoute);
        }
    });
    // tslint:disable-next-line:no-string-literal
    //@ts-ignore
    promise['close'] = cancelOuter;
    return promise as DialogPromise<DialogReturnType<T>>;
}
function availPreloader() {
    let stackCount = 0;
    return async <T extends any>(body: () => Promise<T>) => {
        if (stackCount === 0) showPreloader();
        // console.log("preloader entering", stackCount, stackCount + 1, body.toString());
        stackCount++;
        try {
            const value = await body();
            // console.log("preloader leaving", stackCount, stackCount - 1, body.toString());
            stackCount--;
            if (stackCount === 0)
                await hidePreloader();
            return value;
        } catch (e) {
            // console.log("preloader leaving exception", stackCount, stackCount - 1, body.toString());
            stackCount--;
            if (stackCount === 0)
                await hidePreloader();
            throw e;
        }
    }
}

export function showPreloader() {
    if (!instance) return;
    const lastElement = instance.state.dialogs[instance.state.dialogs.length - 1];
    if (lastElement && lastElement.isPreloader) throw new Error("Can't show preloader while another is active");
    instance.addDialog({
        key: instance.dialogCount + 1,
        isPreloader: true,
        width: "100%",
        defaultClass: "overlay",
        animation: "fade",
        Content: OverlayComponent,
        ok: (returnValue: any) => returnValue,
        cancel: () => undefined,
        props: {},
        showCloseButton: false,
    });
}
export async function hidePreloader() {
    if (!instance) return;
    const lastElement = instance.state.dialogs[instance.state.dialogs.length - 1];
    if (lastElement && !lastElement.isPreloader) {
        console.warn("Can't hide preloader while it is not active");
        return;
    }
    overlayStartTime = performance.now();
    const diff = performance.now() - overlayStartTime;
    if (diff < PRELOADER_ANIMATION_TIMEOUT.enter) {
        await delay(PRELOADER_ANIMATION_TIMEOUT.enter - diff);
        console.log(`${PRELOADER_ANIMATION_TIMEOUT.enter - diff} wait added`);
    }
    instance.removeDialog();
}
function delay(timeout: number) {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            resolve(undefined);
        }, timeout);
    });
}
declare type DialogContainerProps = {
    // history: H.History,
    historyFn: () => H.History<any>;
    onScrollbarShow?: (scrollbarWidth: number) => void,
    onScrollbarHide?: (scrollbarWidth: number) => void
}
declare type DialogContainerState = { dialogs: DialogParams[] }

export default class DialogContainer extends React.Component<DialogContainerProps, DialogContainerState> {
    constructor(props: any) {
        super(props);
        this.state = { dialogs: [] };
    }
    componentDidMount() {
        instance = this;
        const history = this.props.historyFn();
        const location = history.location;
        const queryString = location.search ? location.search.replace("?", "") : ""
        const query = qs.parse(queryString);
        if ("dialog" in query) {
            history.replace(removeQueryString(history, `dialog`));
        }
    }
    get dialogCount() {
        return this.state.dialogs.length;
    }
    addDialog(dialog: DialogParams) {
        let prevCount = this.state.dialogs.length;
        if (prevCount === 0) {
            hideScrollbar(this.props.onScrollbarHide);
        }
        this.state.dialogs.push(dialog);
        this.setState({ dialogs: [...this.state.dialogs] });
        return ++prevCount;
    }
    removeDialog() {
        if (this.state.dialogs.length === 0) return 0;
        let prevCount = this.state.dialogs.length;
        this.state.dialogs.pop();
        this.setState({ dialogs: [...this.state.dialogs] });
        return --prevCount;
    }
    onExited = () => {
        if (this.state.dialogs.length === 0) {
            showScrollbar(this.props.onScrollbarShow);
        }
    }
    render() {
        const history = this.props.historyFn();
        return (
            <React.Fragment>
                <TransitionGroup component={null}>
                    {this.state.dialogs.map(dialog => {
                        if (dialog.isPreloader) return null;
                        const key = dialog.key;
                        const index = typeof (key) === "number" ? key : parseInt(key, undefined);
                        return (
                            <DialogTransition key={`overlay-${key}`} duration={OVERLAY_ANIMATION_TIMEOUT} animation={"fade"} key2={`overlay-${index}`}>
                                <div className="overlay" style={{ zIndex: 100 + index }} >
                                </div>
                            </DialogTransition>
                        )
                    })}
                </TransitionGroup>
                <TransitionGroup component={null}>
                    {this.state.dialogs.map(dialog => {
                        const Component = dialog.Content as any;
                        const index = typeof (dialog.key) === "number" ? dialog.key : parseInt(dialog.key, undefined);
                        const key = `${dialog.isPreloader ? "preloader" : "dialog"}-${dialog.key}`;
                        const timeout = dialog.isPreloader ? PRELOADER_ANIMATION_TIMEOUT : COMP_ANIMATION_TIMEOUT;
                        const defaultClasses = `${dialog.defaultClass} ${dialog.className || ""}`;
                        return (
                            <DialogTransition key={key} duration={timeout} onExited={this.onExited}
                                animation={dialog.animation} key2={key}>
                                <div className={defaultClasses}
                                    style={{ ...dialog.style, zIndex: 100 + Math.abs(index), width: dialog.width }}>
                                    {(!dialog.isPreloader && dialog.showCloseButton) && 
                                        <DialogClose onClose={dialog.cancel} />}
                                    <Component
                                        ok={dialog.ok}
                                        cancel={dialog.cancel}
                                        match={{ params: dialog.props }}
                                        history={history}
                                        appContext={dialog.context}
                                        location={{ pathname: dialog.pathName }}
                                    />
                                </div>
                            </DialogTransition>
                        );
                    })}
                </TransitionGroup>
            </React.Fragment>
        );
    }
}
// tslint:disable-next-line:max-classes-per-file
class OverlayComponent extends React.Component {
    render() {
        return <React.Fragment>
            {/* <button type="button" onClick={e => hideOverlay()}>Hide</button> */}
            <div className="spinner">
                <div className="bounce1" />
                <div className="bounce2" />
                <div className="bounce3" />
            </div>
        </React.Fragment>
    }
}
export const dialogService = {
    showComponent,
    showComponentByRoute,
    showPreloader,
    hidePreloader,
    replaceComponent,
    usePreloader: availPreloader(),
};